import React from 'react';
import { FormattedMessage } from 'react-intl';

export type CascadeCounts = Record<string, number>;

/**
 * Workflow-stage entities the user mentally tracks per project (or per client). Surfaced
 * in cascade-preview modals and the "Includes" column of the Recently Deleted view.
 * Sub-entities (files, comments, payments, transport items) cascade silently as metadata.
 *
 * `projects` is included so client-deletion rows can display "5 projects, …". For
 * standalone project deletions the count is always 1 and shows up as "1 project, …".
 */
const VISIBLE_ENTITY_ORDER = [
    'projects',
    'measures',
    'offers',
    'agreements',
    'products',
    'transports',
    'installations',
    'servicings',
    'impressions',
    'warehouse_records',
] as const;

const ENTITY_KEY_MAP: Record<string, string> = {
    projects: 'contact.cascadeItem.projects',
    measures: 'contact.cascadeItem.measures',
    offers: 'contact.cascadeItem.offers',
    agreements: 'contact.cascadeItem.agreements',
    products: 'contact.cascadeItem.products',
    transports: 'contact.cascadeItem.transports',
    installations: 'contact.cascadeItem.installations',
    servicings: 'contact.cascadeItem.servicings',
    impressions: 'contact.cascadeItem.impressions',
    warehouse_records: 'contact.cascadeItem.warehouseRecords',
};

const DEFAULT_PLURALS: Record<string, string> = {
    projects: '{count, plural, one {# project} other {# projects}}',
    measures: '{count, plural, one {# measure} other {# measures}}',
    offers: '{count, plural, one {# offer} other {# offers}}',
    agreements: '{count, plural, one {# agreement} other {# agreements}}',
    products: '{count, plural, one {# product} other {# products}}',
    transports: '{count, plural, one {# transport} other {# transports}}',
    installations: '{count, plural, one {# installation} other {# installations}}',
    servicings: '{count, plural, one {# servicing} other {# servicings}}',
    impressions: '{count, plural, one {# impression} other {# impressions}}',
    warehouse_records: '{count, plural, one {# warehouse record} other {# warehouse records}}',
};

/**
 * Compose a comma-joined ReactNode listing every non-zero workflow-stage cascade count
 * as a localised plural ("1 measure, 1 offer, 1 agreement, 4 products").
 *
 * Returns null if the project has no cascade dependencies — the caller should fall back
 * to a plain "are you sure?" message in that case.
 */
export function formatCascadeCountsLine(counts: CascadeCounts | null | undefined): React.ReactNode | null {
    if (!counts) return null;
    const items: React.ReactNode[] = [];
    for (const table of VISIBLE_ENTITY_ORDER) {
        const count = counts[table] || 0;
        if (count <= 0) continue;
        items.push(
            <FormattedMessage
                key={table}
                id={ENTITY_KEY_MAP[table]}
                defaultMessage={DEFAULT_PLURALS[table]}
                values={{ count }}
            />
        );
    }
    if (items.length === 0) return null;

    const joined: React.ReactNode[] = [];
    items.forEach((node, i) => {
        if (i > 0) joined.push(', ');
        joined.push(node);
    });
    return <>{joined}</>;
}
